import { ref, readonly } from 'vue';
import { log } from '@/lib/log';

// Single-instance toast queue. Mounts one <ToastStack /> at the app shell;
// any component calls `useToast().show({ type, message })` to add a toast.

export type ToastType = 'info' | 'success' | 'warn' | 'error';

export interface ToastAction {
    label: string;
    run: () => void | Promise<void>;
    /** Defaults to true. Set false when the action should leave the toast open. */
    dismissOnRun?: boolean;
}

export interface Toast {
    id: number;
    type: ToastType;
    message: string;
    /** Auto-dismiss timeout in ms; 0 = sticky. */
    timeoutMs: number;
    action?: ToastAction;
    /** Extra action buttons rendered after `action` (e.g. busy toast's Join + Stop & switch). */
    actions?: readonly ToastAction[];
    /**
     * How many times this exact message has been raised while it was on
     * screen. 1 for the common case; `<ToastStack>` renders `×N` above that.
     */
    count: number;
    /**
     * Optional owner tag. A caller whose toasts stop being true when some
     * state changes (live playback errors, once the user tunes elsewhere)
     * tags them and retires the whole tag with `dismissScope`.
     */
    scope?: string;
}

const queue = ref<Toast[]>([]);
let nextId = 1;
/**
 * Live auto-dismiss timers, keyed by toast id, so a collapsed repeat can
 * restart its host's countdown instead of leaving the original one to expire
 * mid-burst.
 */
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function armTimer(t: Toast): void {
    const running = timers.get(t.id);
    if (running !== undefined) {
        clearTimeout(running);
        timers.delete(t.id);
    }
    if (t.timeoutMs > 0) {
        timers.set(t.id, setTimeout(() => dismiss(t.id), t.timeoutMs));
    }
}

interface ToastOptions {
    timeoutMs?: number;
    action?: ToastAction;
    actions?: readonly ToastAction[];
    scope?: string;
}

type ToastShortcutOptions = number | ToastOptions;

function normaliseOptions(input?: ToastShortcutOptions): ToastOptions {
    if (typeof input === 'number') return { timeoutMs: input };
    return input ?? {};
}

function show(input: ToastOptions & { type?: ToastType; message: string }): number {
    const type = input.type ?? 'info';
    const timeoutMs = input.timeoutMs ?? 4000;
    // Field sweep B6 — the same message raised again while it is still on
    // screen collapses into the entry that is already there. Channel hopping
    // produced four stacked error toasts, two of them the identical string,
    // because the only dedupe was per-channel and hopping changes the channel
    // by definition. The identity that matters to the reader is what the
    // toast SAYS, not which channel raised it.
    const existing = queue.value.find((t) => t.type === type && t.message === input.message);
    if (existing) {
        const merged: Toast = {
            id: existing.id,
            type: existing.type,
            message: existing.message,
            timeoutMs,
            count: existing.count + 1,
            // The newest raise owns the actions: an identical message from a
            // second channel must retry THAT channel, not the one the user
            // has already left.
            ...(input.action ? { action: input.action } : {}),
            ...(input.actions?.length ? { actions: input.actions } : {}),
            ...(input.scope ?? existing.scope ? { scope: input.scope ?? existing.scope } : {}),
        };
        queue.value = queue.value.map((t) => (t.id === existing.id ? merged : t));
        log.debug('toast.repeat', { id: merged.id, type: merged.type, count: merged.count });
        armTimer(merged);
        return merged.id;
    }
    const t: Toast = {
        id: nextId++,
        type,
        message: input.message,
        timeoutMs,
        count: 1,
        ...(input.action ? { action: input.action } : {}),
        ...(input.actions?.length ? { actions: input.actions } : {}),
        ...(input.scope ? { scope: input.scope } : {}),
    };
    queue.value = [...queue.value, t];
    log.debug('toast.show', {
        id: t.id,
        type: t.type,
        hasAction: !!t.action,
        extraActions: t.actions?.length ?? 0,
    });
    armTimer(t);
    return t.id;
}

function dismiss(id: number) {
    const running = timers.get(id);
    if (running !== undefined) {
        clearTimeout(running);
        timers.delete(id);
    }
    queue.value = queue.value.filter((t) => t.id !== id);
}

/**
 * Retire every toast raised under `scope`. For statements that stop being
 * true when the app moves on — a live playback error names a channel, and the
 * moment the user tunes away it is describing something they left behind
 * (field sweep B6; the Phase 6 auto-advance walks up to five channels).
 */
function dismissScope(scope: string) {
    for (const t of queue.value) {
        if (t.scope !== scope) continue;
        const running = timers.get(t.id);
        if (running !== undefined) {
            clearTimeout(running);
            timers.delete(t.id);
        }
    }
    queue.value = queue.value.filter((t) => t.scope !== scope);
}

function clear() {
    for (const timer of timers.values()) clearTimeout(timer);
    timers.clear();
    queue.value = [];
}

export function useToast() {
    return {
        queue: readonly(queue),
        show,
        dismiss,
        dismissScope,
        clear,
        info: (message: string, opts?: ToastShortcutOptions) => show({ type: 'info', message, ...normaliseOptions(opts) }),
        success: (message: string, opts?: ToastShortcutOptions) => show({ type: 'success', message, ...normaliseOptions(opts) }),
        warn: (message: string, opts?: ToastShortcutOptions) => show({ type: 'warn', message, ...normaliseOptions(opts) }),
        error: (message: string, opts?: ToastShortcutOptions) => show({ type: 'error', message, ...normaliseOptions(opts) }),
    };
}
